import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../App';

const API = process.env.REACT_APP_API_URL || 'https://admin.valeaplanning.sbs/api/admin';

const TYPE_COLORS = {
  'RAC_I': { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'RAC_C': { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'RAC_F': { color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  'RAC_P': { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
};

function TypeBadge({ type }) {
  if (!type) return null;
  const cfg = TYPE_COLORS[type] || { color: '#8b92a5', bg: 'rgba(139,146,165,0.15)' };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>
      {type}
    </span>
  );
}

// ─── Shared intervention table ────────────────────────────────────────────────
function InterventionTable({ interventions, exporting, results, onExportOne, onExportAll }) {
  const doneCount    = Object.values(results).filter(r => r.ok).length;
  const pendingCount = interventions.filter(i => !results[i.o_ref]?.ok).length;
  const anyExporting = Object.values(exporting).some(Boolean);

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>
          {interventions.length} commande{interventions.length > 1 ? 's' : ''} détectée{interventions.length > 1 ? 's' : ''}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {doneCount > 0 && (
            <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
              ✓ {doneCount} exportée{doneCount > 1 ? 's' : ''}
            </span>
          )}
          {pendingCount > 0 && (
            <button
              className="btn btn-primary btn-sm"
              onClick={onExportAll}
              disabled={anyExporting}
            >
              {anyExporting ? '⏳…' : `🚀 Tout exporter (${pendingCount})`}
            </button>
          )}
        </div>
      </div>

      {/* Col headers */}
      <div style={{
        display: 'grid', gridTemplateColumns: '160px 1fr 150px 90px 80px 80px 170px',
        padding: '8px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg3)', fontSize: 10, fontWeight: 700,
        color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em',
      }}>
        <div>Réf. commande</div>
        <div>Client / Adresse</div>
        <div>Opérateur</div>
        <div>Type</div>
        <div>Fichiers</div>
        <div>Zoho</div>
        <div style={{ textAlign: 'right' }}>Action</div>
      </div>

      {/* Rows */}
      {interventions.map(itv => {
        const res       = results[itv.o_ref];
        const isLoading = exporting[itv.o_ref];
        const done      = res?.ok;
        const failed    = res && !res.ok;
        return (
          <div key={itv.o_ref} style={{
            display: 'grid', gridTemplateColumns: '160px 1fr 150px 90px 80px 80px 170px',
            padding: '12px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center',
            background: done ? 'rgba(34,197,94,0.04)' : failed ? 'rgba(239,68,68,0.04)' : 'transparent',
          }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700 }}>{itv.o_ref}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{itv.w_ref}</div>
            </div>
            <div style={{ paddingRight: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itv.client_final || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[itv.rue, itv.code_postal, itv.ville].filter(Boolean).join(' ')}
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{itv.operator || '—'}</div>
            <div><TypeBadge type={itv.type} /></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, fontWeight: 600, background: itv.has_workorder ? 'rgba(24,95,165,0.12)' : 'var(--bg3)', color: itv.has_workorder ? '#185FA5' : 'var(--text3)' }}>WO</span>
              <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, fontWeight: 600, background: itv.has_ftel ? 'rgba(34,197,94,0.12)' : 'var(--bg3)', color: itv.has_ftel ? '#16a34a' : 'var(--text3)' }}>FTEL</span>
            </div>
            <div>
              {itv.zoho_exists ? (
                <div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>Existe</span>
                  {itv.zoho_stage && <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 1, maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itv.zoho_stage}</div>}
                </div>
              ) : (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#22c55e' }}>Nouveau</span>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              {done ? (
                <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e' }}>✓ {res.action === 'created' ? 'Créé' : 'Mis à jour'}</span>
              ) : failed ? (
                <span style={{ fontSize: 11, color: '#ef4444' }} title={res.error}>✗ Erreur</span>
              ) : (
                <button
                  className="btn btn-sm"
                  onClick={() => onExportOne(itv)}
                  disabled={isLoading}
                  style={{
                    background: itv.zoho_exists ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',
                    color: itv.zoho_exists ? '#b45309' : '#15803d',
                    border: `1px solid ${itv.zoho_exists ? 'rgba(245,158,11,0.4)' : 'rgba(34,197,94,0.4)'}`,
                    fontSize: 11, fontWeight: 700,
                  }}
                >
                  {isLoading ? '⏳…' : itv.zoho_exists ? '🔄 Mettre à jour' : '🚀 Créer dans Zoho'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ImportCovage() {
  const { admin } = useAuth();

  // Tab : 'dropbox' | 'manual'
  const [tab, setTab] = useState('dropbox');

  // ── Dropbox state ──
  const [dbxFiles, setDbxFiles]         = useState([]);
  const [dbxSelected, setDbxSelected]   = useState(new Set());
  const [dbxLoading, setDbxLoading]     = useState(false);
  const [dbxPreviewing, setDbxPreviewing] = useState(false);
  const [dbxArchiving, setDbxArchiving] = useState(false);
  const [dbxError, setDbxError]         = useState('');

  // ── Shared intervention state ──
  const [interventions, setInterventions] = useState([]);
  const [exporting, setExporting]         = useState({});
  const [results, setResults]             = useState({});
  const [source, setSource]               = useState(null); // 'dropbox' | 'manual'

  // ── Manual upload state ──
  const [manualFiles, setManualFiles]   = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [dragging, setDragging]         = useState(false);
  const [error, setError]               = useState('');
  const fileInputRef = useRef();

  // ── Load Dropbox file list on mount / tab switch ──
  useEffect(() => {
    if (tab === 'dropbox') loadDropboxFiles();
  }, [tab]); // eslint-disable-line

  const loadDropboxFiles = async () => {
    setDbxLoading(true); setDbxError('');
    try {
      const r = await fetch(`${API}/import/dropbox-list`, { headers: { Authorization: `Bearer ${admin.token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setDbxFiles(d.files || []);
      setDbxSelected(new Set()); // reset selection
    } catch(e) {
      setDbxError(e.message);
    } finally {
      setDbxLoading(false);
    }
  };

  const toggleDbxFile = (path) => setDbxSelected(prev => {
    const s = new Set(prev); s.has(path) ? s.delete(path) : s.add(path); return s;
  });
  const selectAllDbx  = () => setDbxSelected(new Set(dbxFiles.map(f => f.path)));
  const selectNoneDbx = () => setDbxSelected(new Set());

  const handleDropboxPreview = async () => {
    if (!dbxSelected.size) return;
    setDbxPreviewing(true); setDbxError(''); setInterventions([]); setResults({});
    const selectedFiles = dbxFiles.filter(f => dbxSelected.has(f.path)).map(f => ({ name: f.name, path: f.path }));
    try {
      const r = await fetch(`${API}/import/dropbox-preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${admin.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ files: selectedFiles }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setInterventions(d.interventions || []);
      setSource('dropbox');
    } catch(e) {
      setDbxError(e.message);
    } finally {
      setDbxPreviewing(false);
    }
  };

  // ── Export one intervention ──
  const exportOne = async (itv) => {
    const key = itv.o_ref;
    setExporting(prev => ({ ...prev, [key]: true }));
    try {
      const r = await fetch(`${API}/import/export-zoho`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${admin.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ o_ref: itv.o_ref, wo_payload: itv.wo_payload, ftel_payload: itv.ftel_payload, zoho_id: itv.zoho_id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setResults(prev => ({ ...prev, [key]: { ok: true, action: d.action } }));
      setInterventions(prev => prev.map(i => i.o_ref === key ? { ...i, zoho_exists: true, zoho_stage: 'A planifier' } : i));
    } catch(e) {
      setResults(prev => ({ ...prev, [key]: { ok: false, error: e.message } }));
    } finally {
      setExporting(prev => ({ ...prev, [key]: false }));
    }
  };

  const exportAll = async () => {
    const todo = interventions.filter(i => !results[i.o_ref]?.ok);
    for (const itv of todo) await exportOne(itv);
  };

  // ── Archive Dropbox files after export ──
  const archiveDropbox = async () => {
    const exportedItvs = interventions.filter(i => results[i.o_ref]?.ok && i.dropbox_paths?.length);
    const paths = exportedItvs.flatMap(i => i.dropbox_paths);
    if (!paths.length) return;

    setDbxArchiving(true);
    try {
      const r = await fetch(`${API}/import/dropbox-archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${admin.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ paths }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      const ok = d.results.filter(r => r.ok).length;
      alert(`✓ ${ok} fichier${ok > 1 ? 's' : ''} archivé${ok > 1 ? 's' : ''} dans "Traitement OK/${d.date}"`);
      // Refresh dropbox list
      await loadDropboxFiles();
      setInterventions([]); setResults({});
    } catch(e) {
      alert('Erreur archivage : ' + e.message);
    } finally {
      setDbxArchiving(false);
    }
  };

  // ── Manual upload ──
  const addFiles = useCallback((newFiles) => {
    const csvs = Array.from(newFiles).filter(f => f.name.toLowerCase().endsWith('.csv'));
    setManualFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...csvs.filter(f => !existing.has(f.name))];
    });
  }, []);

  const handleManualPreview = async () => {
    if (!manualFiles.length) return;
    setManualLoading(true); setError(''); setInterventions([]); setResults({});
    const fd = new FormData();
    manualFiles.forEach(f => fd.append('files', f));
    try {
      const r = await fetch(`${API}/import/preview`, {
        method: 'POST', headers: { Authorization: `Bearer ${admin.token}` }, body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setInterventions(d.interventions || []);
      setSource('manual');
    } catch(e) {
      setError(e.message);
    } finally {
      setManualLoading(false);
    }
  };

  const exportedCount = interventions.filter(i => results[i.o_ref]?.ok && i.dropbox_paths?.length).length;

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Import Commandes Covage</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            Créez ou mettez à jour les commandes Zoho depuis Dropbox ou fichiers CSV
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 10, padding: 4, alignSelf: 'flex-start' }}>
          {[['dropbox', '☁️ Dropbox'], ['manual', '📁 Fichiers manuels']].map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setInterventions([]); setResults({}); }}
              style={{
                padding: '6px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: tab === key ? 'var(--bg2)' : 'none',
                color: tab === key ? 'var(--text)' : 'var(--text3)',
                boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}>{label}</button>
          ))}
        </div>

        {/* ══════════════ ONGLET DROPBOX ══════════════ */}
        {tab === 'dropbox' && (
          <>
            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-secondary btn-sm" onClick={loadDropboxFiles} disabled={dbxLoading}>
                {dbxLoading ? '⏳…' : '↻ Rafraîchir'}
              </button>
              {dbxFiles.length > 0 && (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={selectAllDbx}>Tout sélectionner</button>
                  <button className="btn btn-ghost btn-sm" onClick={selectNoneDbx}>Désélectionner</button>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>{dbxSelected.size} sélectionné{dbxSelected.size > 1 ? 's' : ''}</span>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleDropboxPreview}
                    disabled={!dbxSelected.size || dbxPreviewing}
                    style={{ marginLeft: 'auto' }}
                  >
                    {dbxPreviewing ? '⏳ Chargement…' : `🔍 Analyser (${dbxSelected.size})`}
                  </button>
                </>
              )}
            </div>

            {/* Erreur */}
            {dbxError && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '12px 16px', borderRadius: 10, fontSize: 13 }}>
                ✗ {dbxError}
              </div>
            )}

            {/* Fichiers Dropbox */}
            {dbxLoading ? (
              <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '40px 0', fontSize: 13 }}>Connexion Dropbox…</div>
            ) : dbxFiles.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '40px 0' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>☁️</div>
                <div style={{ fontWeight: 600 }}>Aucun CSV dans le dossier Dropbox</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>/KOSC/WO-RO</div>
              </div>
            ) : (
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg3)', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'grid', gridTemplateColumns: '32px 1fr 80px 100px' }}>
                  <div></div><div>Nom du fichier</div><div>Taille</div><div>Modifié</div>
                </div>
                {dbxFiles.map(f => {
                  const isWO   = f.name.toLowerCase().startsWith('workorder_');
                  const isFTEL = f.name.toLowerCase().includes('ftel') || f.name.toLowerCase().includes('crcmd');
                  const color  = isWO ? '#185FA5' : isFTEL ? '#22c55e' : '#8b92a5';
                  const bg     = isWO ? 'rgba(24,95,165,0.1)' : isFTEL ? 'rgba(34,197,94,0.1)' : 'rgba(139,146,165,0.1)';
                  const sel    = dbxSelected.has(f.path);
                  return (
                    <div key={f.path} onClick={() => toggleDbxFile(f.path)}
                      style={{ display: 'grid', gridTemplateColumns: '32px 1fr 80px 100px', padding: '10px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer', background: sel ? 'rgba(24,95,165,0.06)' : 'transparent', transition: 'background .1s' }}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = 'var(--bg3)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = sel ? 'rgba(24,95,165,0.06)' : 'transparent'; }}
                    >
                      <div>
                        <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${sel ? '#185FA5' : 'var(--border2)'}`, background: sel ? '#185FA5' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {sel && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: bg, color, fontWeight: 700, flexShrink: 0 }}>
                          {isWO ? 'WO' : isFTEL ? 'FTEL' : '?'}
                        </span>
                        <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{(f.size / 1024).toFixed(1)} KB</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{f.modified ? new Date(f.modified).toLocaleDateString('fr-FR') : '—'}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ══════════════ ONGLET MANUEL ══════════════ */}
        {tab === 'manual' && (
          <>
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              style={{ border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border2)'}`, borderRadius: 14, padding: '28px 24px', textAlign: 'center', cursor: 'pointer', background: dragging ? 'rgba(212,43,43,0.04)' : 'var(--bg2)', transition: 'all .2s' }}
            >
              <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
              <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Glissez-déposez vos fichiers CSV</div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>WorkOrder_… et FTEL_…</div>
              <input ref={fileInputRef} type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>

            {error && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '12px 16px', borderRadius: 10, fontSize: 13 }}>✗ {error}</div>
            )}

            {manualFiles.length > 0 && (
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{manualFiles.length} fichier{manualFiles.length > 1 ? 's' : ''}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setManualFiles([]); setInterventions([]); setResults({}); }}>Effacer</button>
                    <button className="btn btn-primary btn-sm" onClick={handleManualPreview} disabled={manualLoading}>
                      {manualLoading ? '⏳ Analyse…' : '🔍 Analyser'}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {manualFiles.map(f => {
                    const isWO = f.name.toLowerCase().startsWith('workorder_');
                    const c = isWO ? '#185FA5' : '#22c55e';
                    const bg = isWO ? 'rgba(24,95,165,0.1)' : 'rgba(34,197,94,0.1)';
                    return (
                      <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '3px 8px', borderRadius: 8, background: bg, color: c, border: `1px solid ${c}30` }}>
                        <span style={{ fontWeight: 600 }}>{isWO ? 'WO' : 'FTEL'}</span>
                        <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        <button onClick={e => { e.stopPropagation(); setManualFiles(prev => prev.filter(x => x.name !== f.name)); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c, padding: 0, fontSize: 13 }}>×</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* ══════════════ TABLE INTERVENTIONS (partagée) ══════════════ */}
        {interventions.length > 0 && (
          <>
            <InterventionTable
              interventions={interventions}
              exporting={exporting}
              results={results}
              onExportOne={exportOne}
              onExportAll={exportAll}
            />

            {/* Bouton archivage Dropbox */}
            {source === 'dropbox' && exportedCount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={archiveDropbox}
                  disabled={dbxArchiving}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  {dbxArchiving ? '⏳ Archivage…' : `📦 Archiver les ${exportedCount} commande${exportedCount > 1 ? 's' : ''} exportée${exportedCount > 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {!dbxLoading && !manualLoading && interventions.length === 0 && manualFiles.length === 0 && tab === 'manual' && (
          <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '40px 0', fontSize: 13 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Aucune commande chargée</div>
            <div>Déposez les fichiers CSV Covage pour commencer</div>
          </div>
        )}
      </div>
      </div>
    </>
  );
}
